import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useContext, useMemo } from 'react';
import { AnalogInput, CalibrationTable, IOExtension, ModBusDevice, ModBusSignal, } from 'z3k-config';
import { sensor_defaults } from './constant';
import { getDeviceInputsOptions } from 'z3k-parser/lib/z3k-selectors';
import { useEditingProps, useEditItem, useGetZ3kObject, } from '../utils/unpack-props';
import { FlagSelector, FlagSelectors, } from 'console/device-settings/z3k/components/material-utils/flag-selector';
import { MaterialBoolControl, MaterialDropdown, NullableRangeField, RangeField, StrictNumberInput, } from 'console/shared/components/fields';
import { AnalogInputCalibrationTable } from '../material-utils/analog-input-calibration-table';
import { useBox } from 'shared/hooks/utils';
import { reactionOptions, supportsAnyReactionOptions } from '../utils/z3k-selectors';
import { Z3KSettings, Z3KSettingsEditor, } from 'console/device-settings/z3k/provider/types';
import { SettingContainer } from '../material-utils/settings-card/container';
import { SettingFullRow } from '../material-utils/settings-card/full-row';
import { MultipleSelectionTable, } from '../material-utils/multiple-selection-table/multiple-selection';
import { Control } from 'console/shared/components/fields/control';
import { TextInputWrapper } from 'console/shared/components/fields/text';
import { IconWithHiddenNameSensor } from 'console/shared/components/fields/icon_sensor_with_hidden_name';
import { getRadioRelays } from 'z3k-parser/lib/sensors/radio';
import { ControlWithoutValidation } from 'shared/components/fields/control/without-validation';
export const AnalogInputComponent = memo(function AnalogInputComponentImpl() {
    const z3k_name = 'analog_inputs';
    const item = useGetZ3kObject(z3k_name);
    const id = item.id;
    const onChange = useEditItem(id, z3k_name);
    const { config, replace_config, replace_device_ui_settings } = useContext(Z3KSettingsEditor);
    const configBox = useBox(config);
    const { device_spec, deviceFirmwareVersion, service_view, serial, device_ui_settings, } = useContext(Z3KSettings);
    const io_extensions = IOExtension.get.all(config);
    const io_radio_relays = getRadioRelays(config);
    const modbus_devices = ModBusDevice.get.all(config);
    const modbus_signals = ModBusSignal.get.all(config);
    const calibration_tables = CalibrationTable.get.all(config);
    const onAddCalibrationTable = useCallback((analog_input) => {
        const [ct_id, new_config] = CalibrationTable.get.add(configBox.current, {
            table: [],
            multiply_x: 10,
        });
        replace_config(AnalogInput.get.applySetterById(new_config, analog_input.id, {
            calibration: ct_id,
        }));
    }, [replace_config]);
    const onChangeType = useCallback((value, object) => replace_config(AnalogInput.get.modifyById(configBox.current, object.id, (analog_input) => {
        const new_ai = {
            ...analog_input,
            ...sensor_defaults.find((sd) => sd.sensor_type == value)
                ?.defaults,
            impulse_frequency: !AnalogInput.a300FrequencySensorTypes.includes(value)
                ? 1
                : object.impulse_frequency,
        };
        return AnalogInput.setUnit(AnalogInput.setNewType(new_ai, value));
    })), [replace_config]);
    const onCalibrationChange = useCallback((calibration, calibration_table_id) => {
        replace_config(CalibrationTable.get.applySetterById(configBox.current, calibration_table_id, {
            table: calibration,
        }));
    }, [replace_config]);
    const valueBox = useBox(item);
    const options = useMemo(() => getDeviceInputsOptions(serial, device_spec, io_extensions, io_radio_relays, false, true, modbus_devices, modbus_signals), [
        device_spec,
        io_extensions,
        io_radio_relays,
        modbus_devices,
        modbus_signals,
    ]);
    const physical_input_num = item.physical_input_num;
    const unit_name = AnalogInput.getUnit(item);
    const isNew = item.is_new;
    const is_voltage_control = useMemo(() => options.find((opt) => opt.value === physical_input_num)
        ?.is_voltage_control, [options, physical_input_num]);
    const sensor_type_options = useMemo(() => sensor_defaults
        .filter(({ min_firmware_version }) => deviceFirmwareVersion >= min_firmware_version)
        .filter(({ sensor_type }) => 
    // Выбираем типы для А300
    device_spec.autoscan.has_a300_inputs
        ? AnalogInput.a300SensorTypes.includes(sensor_type)
        : !AnalogInput.a300SensorTypes.includes(sensor_type))
        .map(({ sensor_type, title }) => ({
        text: title,
        value: sensor_type,
    })), [device_spec]);
    const units_options = useMemo(() => AnalogInput.analog_input_units.map((unit_spec) => ({
        value: unit_spec.value,
        text: `${unit_spec.value_title}${unit_spec.unit !== '' ? ',' : ''} ${unit_spec.unit}`,
    })), [AnalogInput.analog_input_units]);
    const calibration_table = useMemo(() => (calibration_tables || []).find((table) => table.id === item.calibration) ?? null, [calibration_tables, item.calibration]);
    const onChangePhysicalInputNum = useCallback((new_num) => {
        const option = options.find((opt) => opt.value === new_num);
        if (option == null) {
            return;
        }
        let new_value = {
            ...valueBox.current,
            physical_input_num: new_num,
        };
        if (option.is_voltage_control) {
            new_value = AnalogInput.setVoltageControl(new_value, option.text);
        }
        if (option.text.startsWith('Токовый вход')) {
            new_value = AnalogInput.setIsAmperInputType(new_value, true);
        }
        else {
            new_value = AnalogInput.setIsAmperInputType(new_value, false);
        }
        onChange(new_value);
    }, [options, onChange]);
    const onChangeSettingRegister = useCallback((new_params) => {
        onChange({ ...valueBox.current, setting_register: new_params });
    }, [onChange]);
    const onChangeUseCalibrationTable = useCallback((new_value) => {
        if (valueBox.current.calibration == null ||
            valueBox.current.calibration === 0) {
            onAddCalibrationTable({
                ...valueBox.current,
                // sensor_type: AnalogInput.Type.ANALOG_INPUT,
                // setting_register: new_value,
            });
        }
        else {
            onChange({
                ...valueBox.current,
                sensor_type: AnalogInput.Type.ANALOG_INPUT,
                setting_register: new_value,
            });
        }
    }, [onAddCalibrationTable, onChange]);
    const _onChangeType = useCallback((new_type) => onChangeType(new_type, valueBox.current), [onChangeType]);
    const onChangeUseResistance = useCallback((checked) => {
        if (!isNew) {
            return;
        }
        onChange({
            ...AnalogInput.setUseResistance(valueBox.current, checked),
            units: AnalogInput.Units.RESISTANCE,
            upper_threshold: 0,
            lower_threshold: 0,
        });
    }, [onChange]);
    const bits = useMemo(() => [
        {
            bit: AnalogInput.Flags.NO_GUARD_CTRL_ENABLE,
            label: 'Контроль без охраны',
            help_id: 'flag_without_guard',
            show: item.sensor_type !== AnalogInput.Type.THERMOSTAT,
        },
        {
            bit: AnalogInput.Flags.NO_MAIN_SUPPLY_CTRL_ENABLE,
            label: 'Контроль при отсутствии питания',
            help_id: 'analog_inputs_flag_no_feed',
        },
        {
            bit: AnalogInput.Flags.EVENT_TO_SERVER,
            label: 'Событие на сервер при срабатывании',
            help_id: 'flag_event_to_server',
        },
    ], [item.sensor_type]);
    const lowerThresholdEditingProps = useEditingProps(id, z3k_name, 'lower_threshold');
    const upperThresholdEditingProps = useEditingProps(id, z3k_name, 'upper_threshold');
    const inactiveTimeoutEditingProps = useEditingProps(id, z3k_name, 'inactive_level_timeout');
    const activeTimeoutEditingProps = useEditingProps(id, z3k_name, 'active_level_timeout');
    const notifications_actions_options = reactionOptions(config);
    const multipleSelectionTableOptions = useMemo(() => {
        return [
            {
                title: AnalogInput.typesWithThresholdLimits.includes(item.sensor_type)
                    ? 'Выполнить при выходе за верхний порог'
                    : 'Выполнить при срабатывании',
                help_id: 'analog_inputs_notification_list',
                z3k_field: 'upper_notification_list',
                options: notifications_actions_options,
            },
            {
                title: AnalogInput.typesWithThresholdLimits.includes(item.sensor_type)
                    ? 'Выполнить при выходе за нижний порог'
                    : 'Выполнить при обрыве/замыкании шлейфа',
                help_id: 'analog_inputs_notification_list',
                z3k_field: 'notification_list',
                options: notifications_actions_options,
            },
            {
                title: 'Выполнить при восстановлении',
                help_id: 'analog_inputs_recovery_list',
                z3k_field: 'recovery_list',
                options: notifications_actions_options,
            },
        ];
    }, [item.sensor_type]);
    const calibrationEditingProps = useEditingProps(id, z3k_name, 'calibration');
    const nameEditingProps = useEditingProps(id, z3k_name, 'name');
    const physical_input_field = useCallback((input_props) => (_jsx(MaterialDropdown, { ...input_props, options: options })), [options]);
    const sensor_type_field = useCallback((input_props) => (_jsx(MaterialDropdown, { ...input_props, options: sensor_type_options, disabled: !isNew ||
            !service_view ||
            AnalogInput.isUseCalibrationTable(item) })), [
        sensor_type_options,
        isNew,
        service_view,
        AnalogInput.isUseCalibrationTable(item),
    ]);
    const unitsEditingProps = useEditingProps(id, z3k_name, 'units');
    const units_field = useCallback((input_props) => (_jsx(MaterialDropdown, { ...input_props, options: units_options })), [units_options]);
    const impulseFrequencyEditingProps = useEditingProps(id, z3k_name, 'impulse_frequency');
    const impulse_frequency_field = useCallback((input_props) => (_jsx(StrictNumberInput, { ...input_props, step: 1, min: 1, max: 50000 })), []);
    const physicalInputNumEditingProps = useEditingProps(id, z3k_name, 'physical_input_num');
    const sensorTypeEditingProps = useEditingProps(id, z3k_name, 'sensor_type');
    const onChangeWorkingSensor = useCallback((checked) => onChange(AnalogInput.setWorkingSensor(valueBox.current, checked)), [onChange]);
    const multiplyXfield = useCallback((input_props) => (_jsx(MaterialDropdown, { options: [
            { value: 10, text: '0,1' },
            { value: 100, text: '0,01' },
            { value: 1000, text: '0,001' },
        ], ...input_props })), []);
    return (_jsxs(SettingContainer, { children: [!is_voltage_control && (_jsx(Control, { label: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", field: TextInputWrapper, help_id: "analog_inputs_name", ...nameEditingProps })), _jsx(Control, { label: "\u041D\u043E\u043C\u0435\u0440 \u0430\u043F\u043F\u0430\u0440\u0430\u0442\u043D\u043E\u0433\u043E \u0432\u0445\u043E\u0434\u0430", ...physicalInputNumEditingProps, onChange: onChangePhysicalInputNum, field: physical_input_field, help_id: "physical_input_num", help_options: device_spec.io.has_open_collector_outputs }), !is_voltage_control && (_jsx(SettingFullRow, { children: _jsx(Control, { label: "\u0422\u0438\u043F \u0441\u0435\u043D\u0441\u043E\u0440\u0430", ...sensorTypeEditingProps, onChange: _onChangeType, field: sensor_type_field, help_id: "analog_inputs_sensor_type" }) })), AnalogInput.typesWithThresholds.includes(item.sensor_type) &&
                service_view && (_jsxs(_Fragment, { children: [_jsx(NullableRangeField, { label: item.sensor_type === AnalogInput.Type.DISCRETE
                            ? 'Уровень состояния'
                            : 'Порог срабатывания' + ', ' + unit_name, help_id: "analog_inputs_threshold", min_field: lowerThresholdEditingProps, minCaption: item.sensor_type === AnalogInput.Type.DISCRETE
                            ? 'Выкл'
                            : 'Нижний', max_field: upperThresholdEditingProps, maxCaption: item.sensor_type === AnalogInput.Type.DISCRETE
                            ? 'Вкл'
                            : 'Верхний', maxCaptionEnd: true, min: 0, step: item.sensor_type ===
                            AnalogInput.Type.HUMIDITY_SENSOR
                            ? 1
                            : 0.1, max: AnalogInput.getThresholdMax(item) }), AnalogInput.typesWithActivityTimeouts.includes(item.sensor_type) && (_jsx(RangeField, { label: "\u0414\u043B\u0438\u0442\u0435\u043B\u044C\u043D\u043E\u0441\u0442\u044C \u0443\u0440\u043E\u0432\u043D\u044F, \u0441\u0435\u043A", help_id: "analog_inputs_level_timeout", min_field: inactiveTimeoutEditingProps, minCaption: "\u041D\u0435\u0430\u043A\u0442\u0438\u0432", max_field: activeTimeoutEditingProps, maxCaption: "\u0410\u043A\u0442\u0438\u0432", min: 0.2, step: 0.1, max: 60 }))] })), (device_spec.autoscan.has_a300_inputs ||
                deviceFirmwareVersion >= 103) &&
                AnalogInput.Type.ANALOG_INPUT == item.sensor_type && (_jsx(FlagSelector, { value: item.setting_register, onChange: onChangeUseCalibrationTable, flag_label: "\u0418\u0441\u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u044C \u0442\u0430\u0431\u043B\u0438\u0446\u0443 \u043F\u0435\u0440\u0435\u0441\u0447\u0435\u0442\u0430", flag_help_id: "use_calibration_table", mask: AnalogInput.Flags.USE_CALIBRATION_TABLE })), !is_voltage_control ? (_jsxs(_Fragment, { children: [service_view && (_jsx(FlagSelectors, { value: item.setting_register, onChange: onChangeSettingRegister, bits: bits })), item.sensor_type === AnalogInput.Type.ANALOG_INPUT &&
                        service_view && (_jsx(MaterialBoolControl, { disabled: !isNew, color: "secondary", value: AnalogInput.isUseResistance(item), onChange: onChangeUseResistance, label: "\u0420\u0435\u0436\u0438\u043C \u0438\u0437\u043C\u0435\u0440\u0435\u043D\u0438\u044F \u0441\u043E\u043F\u0440\u043E\u0442\u0438\u0432\u043B\u0435\u043D\u0438\u044F", help_id: "analog_inputs_flag_resistance_measure_mode" }))] })) : (_jsx(FlagSelector, { value: item.setting_register, onChange: onChangeSettingRegister, flag_label: "\u0421\u043E\u0431\u044B\u0442\u0438\u0435 \u043D\u0430 \u0441\u0435\u0440\u0432\u0435\u0440 \u043F\u0440\u0438 \u0441\u0440\u0430\u0431\u0430\u0442\u044B\u0432\u0430\u043D\u0438\u0438", flag_help_id: "flag_event_to_server", mask: AnalogInput.Flags.EVENT_TO_SERVER })), _jsx(IconWithHiddenNameSensor, { device_ui_settings: device_ui_settings, item: item, replace_device_ui_settings: replace_device_ui_settings }), _jsx(MaterialBoolControl, { color: "secondary", value: AnalogInput.isWorkingSensor(item), onChange: onChangeWorkingSensor, label: "\u041D\u0435 \u0430\u0432\u0430\u0440\u0438\u0439\u043D\u044B\u0439", help_id: "flag_work_sensor" }), AnalogInput.a300FrequencySensorTypes.includes(item.sensor_type) && (_jsx(FlagSelector, { value: item.setting_register, onChange: onChangeSettingRegister, flag_label: "\u0418\u0441\u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u044C \u0434\u0430\u043D\u043D\u044B\u0435 \u0438\u0437 \u0448\u0438\u043D\u044B \u0434\u0438\u0430\u0433\u043D\u043E\u0441\u0442\u0438\u043A\u0438", flag_help_id: "flag_use_data_from_diagnostics", mask: AnalogInput.Flags.USE_DATA_FROM_DIAGNOSTICS })), supportsAnyReactionOptions(device_spec) && (_jsx(SettingFullRow, { children: _jsx(MultipleSelectionTable, { elements: multipleSelectionTableOptions, z3k_name: z3k_name, notification: false }) })), device_spec.autoscan.has_a300_inputs &&
                AnalogInput.a300FrequencySensorTypes.includes(item.sensor_type) && (_jsx(SettingFullRow, { children: _jsx(Control, { label: "\u041A\u043E\u043B\u0438\u0447\u0435\u0441\u0442\u0432\u043E \u0438\u043C\u043F\u0443\u043B\u044C\u0441\u043E\u0432 \u043D\u0430 \u043E\u0434\u043D\u0443 \u0438\u0437\u043C\u0435\u0440\u044F\u0435\u043C\u0443\u044E \u0435\u0434\u0438\u043D\u0438\u0446\u0443", field: impulse_frequency_field, help_id: "analog_inputs_impulse_frequency", ...impulseFrequencyEditingProps }) })), !is_voltage_control &&
                (device_spec.autoscan.has_a300_inputs ||
                    deviceFirmwareVersion >= 103) &&
                (AnalogInput.isUseCalibrationTable(item) ||
                    item.sensor_type === AnalogInput.Type.ANALOG_INPUT) &&
                item.units !== undefined && (_jsx(SettingFullRow, { children: _jsx(Control, { label: "\u0415\u0434\u0438\u043D\u0438\u0446\u044B \u0438\u0437\u043C\u0435\u0440\u0435\u043D\u0438\u044F", ...unitsEditingProps, field: units_field, help_id: "units" }) })), AnalogInput.isUseCalibrationTable(item) &&
                calibration_table != null &&
                deviceFirmwareVersion >= 472 && (_jsx(ControlWithoutValidation, { help_id: "multiply_x", label: "\u0422\u043E\u0447\u043D\u043E\u0441\u0442\u044C \u0442\u0430\u0431\u043B\u0438\u0446\u044B \u043F\u0435\u0440\u0435\u0441\u0447\u0451\u0442\u0430", value: calibration_table.multiply_x, onChange: (multiply_x) => {
                    const current_multiply = multiply_x / calibration_table.multiply_x;
                    replace_config(CalibrationTable.get.applySetterById(config, calibration_table.id, {
                        multiply_x,
                        table: calibration_table.table.map(({ value, voltage }) => ({
                            value: value,
                            voltage: voltage * current_multiply,
                        })),
                    }));
                }, field: multiplyXfield })), AnalogInput.isUseCalibrationTable(item) &&
                calibration_table != null && (_jsx(SettingFullRow, { children: _jsx(AnalogInputCalibrationTable, { ...calibrationEditingProps, analog_input: item, value: calibration_table.table, onChange: (calibration) => onCalibrationChange(calibration, calibration_table.id), multiply_x: calibration_table.multiply_x }) }))] }));
});
